import {
  AfterViewInit,
  Component,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import dayjs from 'dayjs';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { TranslatePipe } from '@ngx-translate/core';
import { Message } from 'primeng/message';
import { DashboardService } from '../../services/dashboard.service';
import {
  Dashboard,
  DashboardComponentPair,
  createDefaultDashboardConfig,
  normalizeDashboard,
} from '../../models/dashboard';
import { ComponentPair as SmartwatchWidgetComponentPair } from './widgets/smartwatch-dashboard';
import { ComponentPair as JobWidgetComponentPair } from './widgets/job-dashboard';
import { ComponentPair as WorkerWidgetComponentPair } from './widgets/worker-dashboard';
import { ComponentPair as WorkflowWidgetComponentPair } from './widgets/workflow-dashboard';
import { ComponentPair as JobResultAnalyticsWidgetComponentPair } from './widgets/job-result-analytics-dashboard';
import { ComponentPair as AveragePickTimeWidgetComponentPair } from './widgets/average-pick-time-dashboard';
import { ComponentPair as DailyScanTimesWidgetComponentPair } from './widgets/daily-scan-times-dashboard';

_('dashboard.title');

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    HeaderBarComponent,
    LoadingIndicatorComponent,
    Message,
    TranslatePipe,
  ],
})
export class DashboardComponent implements AfterViewInit, OnDestroy {
  private dashboardService = inject(DashboardService);
  private interval?: ReturnType<typeof setInterval> = undefined;
  private renderTimeout?: ReturnType<typeof setTimeout> = undefined;

  @ViewChild('dashboardContainer', { read: ViewContainerRef })
  private containerRef!: ViewContainerRef;

  protected date = dayjs();
  protected isLoading = true;

  static readonly componentRegistry: { [key: string]: DashboardComponentPair } =
    {
      SmartwatchDashboardComponent: SmartwatchWidgetComponentPair,
      JobDashboardComponent: JobWidgetComponentPair,
      WorkerDashboardComponent: WorkerWidgetComponentPair,
      WorkflowDashboardComponent: WorkflowWidgetComponentPair,
      JobResultAnalyticsDashboardComponent:
        JobResultAnalyticsWidgetComponentPair,
      AveragePickTimeDashboardComponent: AveragePickTimeWidgetComponentPair,
      DailyScanTimesDashboardComponent: DailyScanTimesWidgetComponentPair,
    };

  ngAfterViewInit() {
    this.initDashboard();
    this.interval = setInterval(() => {
      this.date = dayjs();
    }, 1000);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
    clearTimeout(this.renderTimeout);
  }

  private initDashboard() {
    this.dashboardService.getDashboard().subscribe((dashboard) => {
      // A synchronous response can arrive while ngAfterViewInit is still being
      // checked. Render in the next task so all bound values change together.
      this.renderTimeout = setTimeout(() => this.renderDashboard(dashboard));
    });
  }

  private renderDashboard(dashboard: Dashboard | null | undefined) {
    const configuration = normalizeDashboard(
      dashboard,
      createDefaultDashboardConfig(),
    ).configuration;

    configuration.items.forEach((itemConfig) => {
      const componentName = itemConfig.component;
      if (DashboardComponent.componentRegistry[componentName] === undefined) {
        console.warn(`Component ${componentName} not found in registry.`);
        return;
      }

      const componentClass =
        DashboardComponent.componentRegistry[componentName].view;
      if (componentClass) {
        const component = this.containerRef.createComponent(componentClass);
        component.location.nativeElement.classList.add(itemConfig.size);
        component.location.nativeElement.classList.add('mb-4');
        component.instance.setConfiguration(itemConfig.configuration);
        component.instance.loadData();
      }
    });

    this.isLoading = false;
  }
}
